import { Observable, of, Subject } from "rxjs";
import { ComponentPortal, ComponentType } from "@angular/cdk/portal";
import { takeUntil } from "rxjs/operators";
import {
    IQuickSettingsMenuPopupOptions,
    ReferenceSelectorPopupComponent
} from "./reference-selector-popup.component";
import { IOverlayResultApi, LgOverlayService } from "@logex/framework/ui-core";
import { FlexibleConnectedPositionStrategy, Overlay, ScrollDispatcher } from "@angular/cdk/overlay";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { ElementRef, inject } from "@angular/core";

export class ReferenceSelectorHost {
    _popupActive = false;

    protected readonly _destroyed$ = new Subject<void>();
    protected _overlayInstance: IOverlayResultApi | null;
    protected _popupInstance: ReferenceSelectorPopupComponent | null;
    protected _popupHidden$: Subject<void> | null;

    protected _scrollDispatcher = inject(ScrollDispatcher);
    protected _overlayService = inject(LgOverlayService);
    protected _overlay = inject(Overlay);
    protected _translateService = inject(LgTranslateService);

    _onDestroy(): void {
        this._hideSettingsPopup();
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    _showSettingsPopup(
        element: ElementRef,
        positionStrategy: FlexibleConnectedPositionStrategy,
        hasBackdrop: boolean,
        options: IQuickSettingsMenuPopupOptions,
        popupComponentType: ComponentType<ReferenceSelectorPopupComponent>
    ): Observable<any> {
        if (this._popupActive) return of(null);

        this._popupHidden$ = new Subject<void>();

        positionStrategy.withScrollableContainers(
            this._scrollDispatcher.getAncestorScrollContainers(element)
        );

        this._overlayInstance = this._overlayService.show({
            onClick: () => this._hideSettingsPopup(), // todo: remove?
            hasBackdrop,
            sourceElement: element,
            positionStrategy,
            onDeactivate: () => {
                if (this._popupInstance) this._popupInstance._isTop = false;
            },
            onActivate: () => {
                if (this._popupInstance) this._popupInstance._isTop = true;
            },
            scrollStrategy: this._overlay.scrollStrategies.reposition({ scrollThrottle: 0 })
        });

        const portal = new ComponentPortal<ReferenceSelectorPopupComponent>(popupComponentType);
        this._popupInstance = this._overlayInstance.overlayRef.attach(portal).instance;

        positionStrategy.positionChanges.pipe(takeUntil(this._popupHidden$)).subscribe(change => {
            this._popupInstance?._updatePosition(change);
        });

        const result$ = this._popupInstance
            .initialize({
                ...options,
                target: element,
                translateService: this._translateService
            })
            .pipe(takeUntil(this._popupHidden$));

        this._popupActive = true;

        return result$;
    }

    protected _hideSettingsPopup(): void {
        if (!this._popupActive) return;

        this._popupActive = false;
        if (this._popupHidden$) {
            this._popupHidden$.next();
            this._popupHidden$.complete();
            this._popupHidden$ = null;
        }
        if (this._overlayInstance) {
            this._overlayInstance.hide();
            this._overlayInstance = null;
        }
        this._popupInstance = null;
    }
}
